import {
  pblLanguages,
  pblLawFirmPositions,
  pblProfessionalQualifications,
  pblServiceAreas,
} from '../../../data/pbl';
import type { PblLawyerProfile, PblServiceArea } from '../../../types/pbl';

import type { VolunteerFormState } from './types';

type ReviewSummary = {
  form: VolunteerFormState;
  serviceAreaLabels: string[];
};

const optionLabel = <Option extends { value: string; defaultLabel: string }>(
  options: readonly Option[],
  value?: string
) => {
  if (!value) return '';
  return options.find((option) => option.value === value)?.defaultLabel || value;
};

const joinLabels = <Option extends { value: string; defaultLabel: string }>(
  options: readonly Option[],
  values?: string[]
) => (values || []).map((value) => optionLabel(options, value)).filter(Boolean).join(', ');

export const getPblServiceAreaLabels = (serviceAreas: PblServiceArea[] = []) =>
  serviceAreas.map((value) => optionLabel(pblServiceAreas, value));

export const buildReviewSummaryFromProfile = (application: PblLawyerProfile): ReviewSummary => {
  const qualificationLabels = joinLabels(pblProfessionalQualifications, application.other_professional_qualifications);
  const languageLabels = joinLabels(pblLanguages, application.language_proficiency);
  const positionLabel = optionLabel(pblLawFirmPositions, application.position_in_law_firm);
  const serviceAreaLabels = getPblServiceAreaLabels(application.service_areas);
  const serviceAreaText = serviceAreaLabels.join(', ');

  return {
    form: {
      full_name_en: application.full_name_en,
      full_name_zh: application.full_name_zh,
      phone: application.phone,
      phone_otp: '',
      phone_otp_verified: false,
      email: application.email,
      email_otp: '',
      email_otp_verified: false,
      password: '',
      password_confirmation: '',
      hk_solicitor_admission_year: String(application.hk_solicitor_admission_year || ''),
      other_professional_qualifications: application.other_professional_qualifications || [],
      other_professional_qualification_text: application.other_professional_qualification_text || '',
      language_proficiency: application.language_proficiency || [],
      language_other_text: application.language_other_text || '',
      law_firm_name: application.law_firm_name,
      law_firm_name_zh: application.law_firm_name_zh || '',
      position_in_law_firm: application.position_in_law_firm,
      position_other_text: application.position_other_text || '',
      service_areas: application.service_areas,
      cv_publication_consent: application.cv_publication_consent,
      cv_full_name_en: application.cv_full_name_en || application.full_name_en,
      cv_full_name_zh: application.cv_full_name_zh || application.full_name_zh,
      cv_law_firm_en: application.cv_law_firm_en || application.law_firm_name,
      cv_law_firm_zh: application.cv_law_firm_zh || application.law_firm_name,
      cv_position_en: application.cv_position_en || positionLabel,
      cv_position_zh: application.cv_position_zh || positionLabel,
      cv_admission_year_en: application.cv_admission_year_en || String(application.hk_solicitor_admission_year || ''),
      cv_admission_year_zh: application.cv_admission_year_zh || String(application.hk_solicitor_admission_year || ''),
      cv_other_qualifications_en: application.cv_other_qualifications_en || qualificationLabels,
      cv_other_qualifications_zh: application.cv_other_qualifications_zh || qualificationLabels,
      cv_languages_en: application.cv_languages_en || languageLabels,
      cv_languages_zh: application.cv_languages_zh || languageLabels,
      cv_primary_practice_areas_en: application.cv_primary_practice_areas_en || serviceAreaText,
      cv_primary_practice_areas_zh: application.cv_primary_practice_areas_zh || serviceAreaText,
      cv_professional_experience_en: application.cv_professional_experience_en || '',
      cv_professional_experience_zh: application.cv_professional_experience_zh || '',
      cv_pro_bono_experience_en: application.cv_pro_bono_experience_en || '',
      cv_pro_bono_experience_zh: application.cv_pro_bono_experience_zh || '',
      cv_signature: application.cv_signature || '',
      cv_signed_at: application.cv_signed_at || '',
      firm_authorisation_template_downloaded: application.firm_authorisation_template_downloaded,
      firm_authorisation_file_id: application.firm_authorisation_file_id,
      firm_authorisation_confirmed: application.firm_authorisation_confirmed,
      firm_authorisation_letter_attached: application.firm_authorisation_letter_attached,
      law_firm_change_notify_agreed: application.law_firm_change_notify_agreed,
      service_pledge_agreed: application.service_pledge_agreed,
      post_completion_agreed: application.post_completion_agreed,
      pics_agreed: application.pics_agreed,
      bankrupt_or_insolvent: application.bankrupt_or_insolvent,
      arrangement_with_creditors: application.arrangement_with_creditors,
      criminal_conviction: application.criminal_conviction,
      professional_misconduct_or_pending_disciplinary:
        application.professional_misconduct_or_pending_disciplinary,
      suspended_from_professional_practice: application.suspended_from_professional_practice,
      conduct_supporting_document_file_id: application.conduct_supporting_document_file_id,
      declaration_signature: application.declaration_signature,
      declaration_signed_at: application.declaration_signed_at,
      review_confirmed: application.review_confirmed,
    },
    serviceAreaLabels,
  };
};
